import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";

const navItems = [
  { path: "/", label: "Realizacje", num: "01" },
  { path: "/studio", label: "Studio", num: "02" },
  { path: "/warsztaty", label: "Warsztaty", num: "03" },
  { path: "/kontakt", label: "Kontakt", num: "04" },
];

export const Navbar = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [time, setTime] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      const d = new Date();
      const opts = { timeZone: "Europe/Warsaw", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false };
      setTime(d.toLocaleTimeString("pl-PL", opts));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#F2EEE5]/85 backdrop-blur-md border-b border-black/10" : "bg-transparent"}`}>
        <div className="max-w-[1800px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 bg-[#0E0E0E] text-[#F2EEE5] flex items-center justify-center serif italic font-black text-xl group-hover:bg-[#FF4D2E] transition-colors duration-500">A</div>
            <div className="leading-none">
              <div className="serif font-black text-[19px] tracking-tight">ANIMUSZ</div>
              <div className="mono text-[9px] tracking-[0.25em] uppercase text-black/60">Studio · est.</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const active = pathname === item.path;
              return (
                <Link key={item.path} to={item.path} className={`group relative px-4 py-2 flex items-center gap-2 transition-colors ${active ? "text-[#FF4D2E]" : "text-[#0E0E0E]"}`}>
                  <span className="mono text-[10px] opacity-50">{item.num}</span>
                  <span className="link-underline text-[15px] font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <div className="mono text-[11px] text-black/60">ŁÓDŹ / {time}</div>
            <Link to="/kontakt" className="group flex items-center gap-2 bg-[#0E0E0E] text-[#F2EEE5] px-4 py-2.5 hover:bg-[#FF4D2E] transition-colors duration-300">
              <span className="text-sm font-medium">Zamów projekt</span>
              <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-500" />
            </Link>
          </div>

          <button onClick={() => setOpen(true)} className="md:hidden p-2" aria-label="Menu">
            <Menu size={26} />
          </button>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[60] bg-[#0E0E0E] text-[#F2EEE5] md:hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
            <div className="serif font-black text-xl">ANIMUSZ</div>
            <button onClick={() => setOpen(false)} className="p-2"><X size={26} /></button>
          </div>
          <nav className="px-6 py-10 flex flex-col gap-6">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setOpen(false)} className="flex items-baseline gap-4 group">
                <span className="mono text-xs opacity-50">{item.num}</span>
                <span className="display text-5xl group-hover:text-[#FF4D2E] transition-colors">{item.label}</span>
              </Link>
            ))}
          </nav>
          <div className="absolute bottom-6 left-6 right-6 mono text-[11px] opacity-60 flex justify-between">
            <span>ŁÓDŹ / PL</span><span>{time}</span>
          </div>
        </div>
      )}
    </>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#0E0E0E] text-[#F2EEE5] mt-32">
      <div className="overflow-hidden py-10 border-b border-white/10">
        <div className="flex whitespace-nowrap marquee-track">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="flex items-center gap-8 mr-8">
              <span className="display text-[8vw] leading-none">Animusz Studio</span>
              <span className="serif italic text-[6vw] text-[#FF4D2E]">*</span>
              <span className="display text-[8vw] leading-none text-outline" style={{WebkitTextStroke: "1.5px #F2EEE5"}}>animacja filmowa</span>
              <span className="serif italic text-[6vw] text-[#FF4D2E]">*</span>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-[1800px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className="mono text-[10px] tracking-[0.25em] opacity-50 mb-4">KONTAKT</div>
          <a href="mailto:animuszstudio@gmail.com" className="block text-lg mb-1 link-underline w-fit">animuszstudio@gmail.com</a>
          <a href="tel:+48796633631" className="block text-lg mono">+48 796 633 631</a>
        </div>
        <div>
          <div className="mono text-[10px] tracking-[0.25em] opacity-50 mb-4">ADRES</div>
          <p className="text-lg leading-tight">Magdalena Parszewska<br />ul. Piotrkowska 132/5U<br />90-062 Łódź, Polska</p>
        </div>
        <div>
          <div className="mono text-[10px] tracking-[0.25em] opacity-50 mb-4">NAWIGACJA</div>
          {navItems.map(i => <Link key={i.path} to={i.path} className="block text-lg mb-1 link-underline w-fit">{i.label}</Link>)}
        </div>
        <div>
          <div className="mono text-[10px] tracking-[0.25em] opacity-50 mb-4">SPOŁECZNOŚĆ</div>
          <a className="block text-lg mb-1 link-underline w-fit" href="#">Instagram</a>
          <a className="block text-lg mb-1 link-underline w-fit" href="#">Vimeo</a>
          <a className="block text-lg mb-1 link-underline w-fit" href="#">YouTube</a>
        </div>
      </div>
      <div className="max-w-[1800px] mx-auto px-6 md:px-10 py-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 mono text-[11px] opacity-60">
        <span>© {new Date().getFullYear()} Animusz Studio. Wszystkie prawa zastrzeżone.</span>
        <span>Studio animacji filmowej / Łódź</span>
      </div>
    </footer>
  );
};

export const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
};

export const BlobCursor = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const el = ref.current;
    const move = (e) => {
      if (el) { el.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`; }
    };
    const over = (e) => {
      if (e.target.closest("a, button, .pf-card, [data-hover]")) el.classList.add("hover");
      else el.classList.remove("hover");
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseover", over); };
  }, []);
  return <div ref={ref} className="blob-cursor hidden md:block" />;
};

export const MarqueeBanner = ({ children, className = "" }) => (
  <div className={`overflow-hidden ticker-line py-4 ${className}`}>
    <div className="flex whitespace-nowrap marquee-slow">
      {Array(6).fill(0).map((_, i) => (
        <div key={i} className="flex items-center gap-6 mr-6">
          {children}
        </div>
      ))}
    </div>
  </div>
);
